import { Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { Menu, X, Moon, Sun, ChevronRight } from 'lucide-react'
import { Logo } from '@/components/ui/Logo'
import { siteConfig } from '@/config/site'
import { navItems } from '@/config/navigation'
import { useTheme } from '@/hooks/useTheme'
import { buttonVariants } from '@/components/ui/Button'
import { cn } from '@/lib/cn'

export function Navbar() {
  const { theme, toggle } = useTheme()
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  // Subtle border on scroll
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8)
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [])

  // Close mobile drawer on resize to tablet/desktop
  useEffect(() => {
    const handler = () => {
      if (window.innerWidth >= 768) setMenuOpen(false)
    }
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  // Close mobile menu on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <header
      className={cn(
        'sticky top-0 z-50 transition-colors duration-200 backdrop-blur-md bg-surface-0/95',
        scrolled ? 'border-b border-border-subtle shadow-xs' : 'border-b border-transparent'
      )}
    >
      <nav
        className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 md:px-8 py-3 sm:py-4"
        aria-label="Main navigation"
      >
        {/* Brand Wordmark with Emblem */}
        <Link
          to="/"
          className="flex items-center gap-2.5 text-lg font-bold tracking-tight text-fg transition-opacity hover:opacity-80 active:opacity-70 py-1"
          aria-label={`${siteConfig.shortName} — Home`}
          onClick={() => setMenuOpen(false)}
        >
          <Logo size={24} />
          <span>{siteConfig.shortName}</span>
        </Link>

        {/* Desktop Links */}
        <ul className="hidden md:flex items-center gap-8 list-none m-0 p-0">
          {navItems.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                className="text-sm font-medium text-fg-subtle hover:text-fg transition-colors duration-150"
                activeProps={{ className: 'text-fg font-semibold' }}
                activeOptions={{ exact: item.to === '/' }}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Actions (Theme toggle + Quote CTA + Hamburger) */}
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={toggle}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            className="flex items-center justify-center w-10 h-10 min-w-[40px] rounded-sm text-fg-muted hover:text-fg hover:bg-surface-2 active:bg-surface-3 transition-colors duration-150 cursor-pointer"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <Link
            to="/contact"
            className={buttonVariants({
              variant: 'primary',
              size: 'sm',
              className: 'hidden md:inline-flex',
            })}
          >
            Get a Quote
          </Link>

          {/* Hamburger toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={menuOpen}
            className="flex md:hidden items-center justify-center w-10 h-10 min-w-[40px] rounded-sm text-fg hover:bg-surface-2 active:bg-surface-3 transition-colors duration-150 cursor-pointer"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile Backdrop */}
      {menuOpen && (
        <div
          className="fixed inset-0 top-[57px] sm:top-[65px] z-40 bg-surface-0/60 backdrop-blur-xs md:hidden"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Drawer */}
      {menuOpen && (
        <div
          className={cn(
            'fixed inset-x-0 top-[57px] sm:top-[65px] z-50 border-b border-border-subtle bg-surface-0 md:hidden',
            'px-4 sm:px-6 py-5 shadow-xl max-h-[calc(100dvh-57px)] sm:max-h-[calc(100dvh-65px)] overflow-y-auto'
          )}
        >
          <div className="flex flex-col gap-1">
            <span className="font-mono text-[10px] uppercase tracking-widest text-fg-muted mb-1 px-3">
              Navigation
            </span>
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center justify-between px-3.5 py-3 text-base font-medium text-fg-subtle rounded-sm hover:text-fg hover:bg-surface-2 active:bg-surface-3 transition-colors duration-150"
                activeProps={{ className: 'text-fg font-semibold bg-surface-2' }}
                activeOptions={{ exact: item.to === '/' }}
                onClick={() => setMenuOpen(false)}
              >
                <span>{item.label}</span>
                <ChevronRight size={16} className="text-fg-muted" />
              </Link>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-border-subtle flex flex-col gap-3">
            <Link
              to="/contact"
              className={buttonVariants({
                variant: 'primary',
                size: 'md',
                fullWidth: true,
              })}
              onClick={() => setMenuOpen(false)}
            >
              Get a Free Quote
            </Link>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 px-1 pt-2 text-xs text-fg-muted">
              <span>{siteConfig.location}</span>
              <a
                href={`mailto:${siteConfig.email}`}
                className="hover:text-fg underline underline-offset-2 transition-colors py-1"
              >
                {siteConfig.email}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
